import type { ReactNode } from "react";
import {
  type getAccountId,
  baseFetch,
  getSessionAuthorizationHeader,
  getUrlWithSearchParams,
} from "@helium10/re-core";
import type { IDataTable } from "@helium10/re-ui-components";
import { getHeaderHelper } from "@/core/helpers/getHeader.helper";
import { packfeeLink } from "@/core/apiLink";
import type { DiscrepancyType, PackingType, ProductType } from "../utils";
import { type StringifiableRecord } from "query-string";

export interface IResponse<T> {
  code: number;
  data: T;
  message: string;
}
// 基础请求参数
export interface IBaseRequestParams {
  fbaFeeSearch: string;
  pageIndex: number;
  pageSize: number;
  orderBy: keyof IDataTable;
  ifDesc: boolean;
}
// all product请求参数
export interface IProductParams extends IBaseRequestParams {
  accountId: ReturnType<typeof getAccountId>;
  sellerMarkets: { seller: string; market: string }[];
  missingProduct?: boolean;
}
// claims list请求参数
export type IClaimsParams = IProductParams;
// 返回Item数据
export interface IPack {
  id: string;
  sellerId: string;
  sellerName: string;
  marketplaceId: string;
  countryCode: string;
  asin: string;
  fnsku: string;
  sku: string;
  product?: string;
  productName?: string; // main list api no the field
  title?: ReactNode;
  imageUrl: string;
  updatedAt?: string;
  source?: string;
  amazonDimensionsLongSide?: number;
  actualDimensionsLongSide?: number;
  amazonDimensionsMiddleSide?: number;
  actualDimensionsMiddleSide?: number;
  amazonDimensionsShortSide?: number;
  actualDimensionsShortSide?: number;
  amazonDimensionUnit?: string;
  actualDimensionUnit?: string;
  amazonWeight?: number;
  actualWeight?: number;
  amazonWeightUnit?: string;
  actualWeightUnit?: string;
  productType: ProductType;
  packingType: PackingType;
  claimableAmount: number;
}
export interface IClaim extends IPack {
  operational: boolean;
  businessId: string;
  discrepancyType?: DiscrepancyType;
  updateRecordLoading?: boolean;
  updatePackTypeLoading?: boolean;
}
export interface IProductResponse {
  code: number;
  message: string;
  data: {
    data: IPack[];
    totalCount: number;
  };
}
export interface IClaimsResponse {
  code: number;
  message: string;
  data: {
    data: IClaim[];
    totalCount: number;
  };
}

// -----------
export type IActualProductInfo = Pick<
  IPack,
  "product" | "imageUrl" | "marketplaceId" | "asin" | "sku" | "fnsku" | "sellerId" | "product" // 目前返回的数据没有sellerId
> & {
  id: string;
  title: string;
  reason: string;
  longestSide: number;
  medianSide: number;
  shortestSide: number;
  dimensionUnit: string;
  weight: number;
  weightUnit: string;
  successful: boolean;
};

export interface IUploadResponseInfo {
  succeedDetails: IActualProductInfo[];
  failedDetails: IActualProductInfo[];
  attachId: number;
  module: string;
}

// 每次进入页面的请求,控制遮罩
export const getFirstEntry = async (
  params: { accountId: ReturnType<typeof getAccountId> },
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<null, IResponse<boolean>>({
    method: "GET",
    url: getUrlWithSearchParams(packfeeLink.firstEntry, params),
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    signal: extraArgument?.signal,
  });
};

// all product 列表数据
export const getProductList = async (
  params: IProductParams,
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<IProductParams, IProductResponse>({
    method: "POST",
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    url: packfeeLink.productList,
    params: params,
    signal: extraArgument?.signal,
  });
};

// 导出all product 列表数据 -- 未知 - 主表右上角导出
export const exportProductList = async (params: IProductParams) => {
  return fetch(packfeeLink.productListExport, {
    method: "POST",
    headers: new Headers({
      "Content-Type": "application/json",
      ...(await getSessionAuthorizationHeader()),
    }),
    body: JSON.stringify({ ...params }),
  });
};

// 下载上传模板
export const downTemplate = async (params: object) => {
  return fetch(packfeeLink.downTemplate, {
    method: "POST",
    headers: new Headers({
      "Content-Type": "application/json",
      ...(await getSessionAuthorizationHeader()),
    }),
    body: JSON.stringify({ ...params }),
  });
};

// 上传文件保存
export interface IPackfeeUploadFileParams {
  file: { fileBlob: Blob; fileName: string };
  accountId: ReturnType<typeof getAccountId>;
}
// 上传单个文件
export const packfeeUploadFile = (params: IPackfeeUploadFileParams) => {
  return baseFetch<IPackfeeUploadFileParams, IResponse<IUploadResponseInfo>>({
    method: "POST",
    isFormData: true,
    url: packfeeLink.uploadFile,
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    params,
  });
};

export type IHistoryParams = {
  accountId: ReturnType<typeof getAccountId>;
  pageIndex: number;
  pageSize: number;
  orderBy: keyof IDataTable;
  ifDesc: boolean;
};
export interface IHistoryItem extends IDataTable {
  module: string;
  fileName: string;
  contentType: string;
  fileOosUrl: string;
  successCount: number;
  failedCount: number;
  userId: number;
  createTime: string;
  updateTime: string;
}
export type IHistoryData = {
  totalCount: number;
  totalData: number;
  data: IHistoryItem[];
};

// 获取上传历史列表数据
export const getUploadHistoryList = async (
  params: IHistoryParams,
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<IHistoryParams, IResponse<IHistoryData>>({
    method: "POST",
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    url: packfeeLink.uploadHistoryList,
    params,
    signal: extraArgument?.signal,
  });
};

export interface IDownFileParams {
  accountId: ReturnType<typeof getAccountId>;
  id: number;
  ifInstant: boolean;
}

// 下载已上传的数据
export const downHistoryFile = async (params: IDownFileParams) => {
  return fetch(packfeeLink.downFile, {
    method: "POST",
    headers: new Headers({
      "Content-Type": "application/json",
      ...(await getSessionAuthorizationHeader()),
    }),
    body: JSON.stringify({ ...params }),
  });
};

// Pending Claims 列表数据
export const getClaimsList = async (
  params: IClaimsParams,
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<IClaimsParams, IClaimsResponse>({
    method: "POST",
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    url: packfeeLink.claimsList,
    params,
    signal: extraArgument?.signal,
  });
};

// 更新Claim数据
export interface IUpdateResponse {
  success: boolean;
  skus: string[];
  discrepancyType: DiscrepancyType;
  countryCodes: null | string;
}
interface IUpdateParams {
  id?: string;
  accountId: ReturnType<typeof getAccountId>;
  sellerId: string;
  countryCode: string;
  asin: string;
  fnsku: string;
  sku: string;
  businessId: string;
  updateTipHandler?: (skus: string[]) => void;
  errorTipHandler?: () => void;
}
export type IUpdatePackTypeParams = IUpdateParams & {
  packingType: PackingType;
};
export type IUpdateRecordParams = IUpdateParams & {
  longestSide: string;
  medianSide: string;
  shortestSide: string;
  dimensionUnit: string;
  weight: string;
  weightUnit: string;
};

export const updatePackType = (
  params: IUpdatePackTypeParams,
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<IUpdatePackTypeParams, IResponse<IUpdateResponse>>({
    method: "POST",
    url: packfeeLink.updatePackTypeLink,
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    params,
    signal: extraArgument?.signal,
  });
};

export const updateRecord = (
  params: IUpdateRecordParams,
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<IUpdateRecordParams, IResponse<IUpdateResponse>>({
    method: "POST",
    url: packfeeLink.updateRecordLink,
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    params,
    signal: extraArgument?.signal,
  });
};

// 确认弹窗
export interface IClaimDetailParams extends StringifiableRecord {
  id?: string;
  accountId: ReturnType<typeof getAccountId>;
  sellerId: string;
  countryCode: string;
  asin: string;
  fnsku: string;
  sku: string;
  businessId: string;
  amazonDimensionsLongSide?: number;
  amazonDimensionsMiddleSide?: number;
  amazonDimensionsShortSide?: number;
  amazonDimensionUnit?: string;
  amazonWeight?: number;
  amazonWeightUnit?: string;
  // checkTipHandler?: () => void;
}
export type IClaimDetailOuterParams = IClaimDetailParams & {
  checkTipHandler?: () => void;
  failedTip?: () => void;
};
export interface IClaimsConfirm {
  discrepancyType: DiscrepancyType;
  asinCountryList: string[];
  moreThan20: boolean;
}
// 展示详情
export interface IClaimDetail
  extends Pick<
    IClaim,
    | "id"
    | "sellerId"
    | "countryCode"
    | "marketplaceId"
    | "product"
    | "productName"
    | "asin"
    | "sku"
    | "fnsku"
    | "imageUrl"
    | "businessId"
    | "productType"
    | "packingType"
    | "discrepancyType"
    | "amazonDimensionsLongSide"
    | "amazonDimensionsMiddleSide"
    | "amazonDimensionsShortSide"
    | "amazonDimensionUnit"
    | "amazonWeight"
    | "amazonWeightUnit"
  > {
  asinCountryList: string[];
  fnskuSkuList: string[];
  longestSide: number;
  medianSide: number;
  shortestSide: number;
  dimensionUnit: string;
  weight: number;
  weightUnit: string;
  salesPrice: number;
  estimatedClaimableAmount: number;
}
export const getClaimDetail = (
  params: IClaimDetailParams,
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<IClaimDetailParams, IResponse<IClaimDetail>>({
    method: "GET",
    url: getUrlWithSearchParams(packfeeLink.claimDetailLink, params),
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    signal: extraArgument?.signal,
  });
};

// 确认 claim
export const setClaimConfirm = (
  params: IClaimDetailParams,
  extraArgument?: { signal?: AbortSignal },
) => {
  return baseFetch<IClaimDetailParams, IResponse<IClaimsConfirm>>({
    method: "POST",
    url: packfeeLink.claimConfirmLink,
    headers: getHeaderHelper(),
    useSessionAccountId: true,
    params,
    signal: extraArgument?.signal,
  });
};
